import { Check, Circle } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';

import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_RULES,
  passwordStrength,
  type StrengthLevel,
} from '../lib/passwordStrength';

const BAR_COLOURS: Record<StrengthLevel, string> = {
  weak: 'bg-red-500',
  medium: 'bg-saffron-500',
  strong: 'bg-brand-500',
  very_strong: 'bg-brand-700',
};

const LABEL_COLOURS: Record<StrengthLevel, string> = {
  weak: 'text-red-600',
  medium: 'text-saffron-600',
  strong: 'text-brand-600',
  very_strong: 'text-brand-700',
};

/**
 * A four-segment strength bar and the checklist behind it, updated per keystroke.
 *
 * The checklist is always visible - even before typing - so the rules are known
 * up front rather than discovered one error at a time.
 */
export function PasswordStrengthMeter({ value, id }: { value: string; id?: string }) {
  const t = useT();
  const strength = passwordStrength(value);

  return (
    <div id={id} className="mt-2 space-y-1.5">
      <div className="flex items-center gap-3">
        <div className="flex flex-1 gap-1.5" aria-hidden>
          {[1, 2, 3, 4].map((segment) => (
            <span
              key={segment}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors duration-200',
                strength.level && segment <= strength.score
                  ? BAR_COLOURS[strength.level]
                  : 'bg-line',
              )}
            />
          ))}
        </div>
        <p
          aria-live="polite"
          className={cn(
            // Fixed line box: an empty label would collapse and the row would grow on the first keystroke.
            'h-4 w-[4.75rem] shrink-0 text-right text-xs leading-4 font-semibold',
            strength.level ? LABEL_COLOURS[strength.level] : 'text-muted',
          )}
        >
          {strength.level ? (
            <>
              <span className="sr-only">{t('auth.strength.sr_label')} </span>
              {t(`auth.strength.${strength.level}`)}
            </>
          ) : null}
        </p>
      </div>

      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {PASSWORD_RULES.map((rule) => {
          const met = strength.met[rule.id];
          return (
            <li
              key={rule.id}
              data-met={met}
              className={cn(
                'flex items-center gap-1 text-xs transition-colors',
                met ? 'text-brand-700' : 'text-muted',
              )}
            >
              {met ? (
                <Check className="size-3 shrink-0 text-brand-600" strokeWidth={3} aria-hidden />
              ) : (
                <Circle className="size-3 shrink-0 text-line" aria-hidden />
              )}
              {t(`auth.strength.rule_${rule.id}`, { min: MIN_PASSWORD_LENGTH })}
              <span className="sr-only"> {met ? t('auth.strength.done') : t('auth.strength.missing')}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
